/*
 * Inbox (draft 8): one conversation per contact. The list on the left, the conversation on the right, a reply box that
 * sends from the business line (what you type is what goes), office-only notes, and Done. Eunice's practice replies and
 * flags show as notes.
 *
 * Fixed 2026-10-30 (owner: "inbox is pretty broken and buggy"): the page fits the window and the list and the chat scroll
 * on their own, long links no longer spill out of the list, a reply typed for one person no longer carries over to the
 * next, the chat opens at the newest message, opening a conversation clears its count right away, Mark done moves on to
 * the next one, and on a phone the list and the conversation take turns.
 *
 * Looks like Quo (owner, 2026-11-03, "improve ui for inbox, this is how quo looks"): one panel, a circle with initials on
 * each row, Open, Done and Unread chips, the time between messages as a centered stamp, our texts in solid brand color on
 * the right, theirs grey on the left, and the reply box as one rounded box with Send inside.
 */
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { asksForPlan, type PlanOfferOpener, type ThreadRow } from "@steward/shared";
import { useClientThreads, useReply, useThread, useThreadChange, useThreads, useUndoReply } from "../api/hooks";
import { useSession } from "../app/session";
import { Icon } from "../components/Icon";
import { useToast } from "../components/overlays";
import { PlanOfferSend } from "../components/PlanOffer";
import {
  Avatar,
  ago,
  Banner,
  Button,
  Chip,
  EmptyState,
  LoadError,
  Page,
  prettyPhone,
  Skeleton,
  StatusLabel,
} from "../components/parts";
import { errText } from "../components/work";
import { SettingsButton } from "./settings/Feature";

/** Two columns side by side from this width up (matches .inbox in app.css). */
const WIDE = "(min-width: 641px)";

/** "now", "5m", "3h", "2d", then the date: short enough to sit beside a name. */
function shortAgo(iso: string, now = Date.now()): string {
  const m = Math.floor((now - Date.parse(iso)) / 60_000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  if (m < 60 * 24) return `${Math.floor(m / 60)}h`;
  if (m < 60 * 24 * 7) return `${Math.floor(m / (60 * 24))}d`;
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: d.getFullYear() === new Date(now).getFullYear() ? undefined : "numeric",
  });
}

/** A name to show: a lead with no name yet shows their number written the usual way. */
function who(name: string): string {
  return /[a-z]/i.test(name) ? name : prettyPhone(name);
}

/** Initials on a color, or a plain person when there is no name yet (a number or an email). */
function WhoCircle({ name, large = false }: { name: string; large?: boolean }) {
  return /[a-z]/i.test(name) && !name.includes("@") ? (
    <Avatar name={name} large={large} />
  ) : (
    <span className={large ? "avatar large inbox-nobody" : "avatar inbox-nobody"} aria-hidden>
      <Icon name="person" size={large ? 22 : 18} />
    </span>
  );
}

/** "Today 5:02 PM", "Yesterday 3:27 PM", "Tue 9:10 AM", "Oct 28 9:10 AM": the stamp between messages. */
function stamp(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(now) - day(d)) / 86_400_000);
  if (days === 0) return `Today ${time}`;
  if (days === 1) return `Yesterday ${time}`;
  if (days < 7) return `${d.toLocaleDateString("en-US", { weekday: "short" })} ${time}`;
  const date = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: d.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
  return `${date} ${time}`;
}

/** A new stamp when an hour or more passed since the message before, like a phone's texts. */
const GAP = 60 * 60_000;

/** open: a conversation to open first (from a link, like a receptionist reply). */
export function Inbox({ open }: { open?: string } = {}) {
  const [status, setStatus] = useState<"open" | "done">("open");
  const [picked, setPicked] = useState<string | null>(open ?? null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const list = useThreads(status);
  // Unread keeps the open one in the list, so reading it does not pull it out from under you.
  const threads = unreadOnly ? list.data?.threads.filter((t) => t.unread > 0 || t.id === picked) : list.data?.threads;

  // On a wide screen the first conversation opens by itself, like Jobber and every mail app.
  useEffect(() => {
    if (picked || !threads?.length || !window.matchMedia(WIDE).matches) return;
    setPicked(threads[0]!.id);
  }, [picked, threads]);

  const show = (next: "open" | "done") => {
    if (next === status) return;
    setStatus(next);
    setPicked(null);
  };
  /** After Mark done (or Open again) the conversation leaves this list, so the next one opens in its place. */
  const moveOn = (id: string) => {
    if (!threads) {
      setPicked(null);
      return;
    }
    const at = threads.findIndex((t) => t.id === id);
    const next = threads[at + 1] ?? threads[at - 1];
    setPicked(next && window.matchMedia(WIDE).matches ? next.id : null);
  };

  return (
    <Page title="Inbox" fill secondaryAction={<SettingsButton to="/receptionist">Receptionist</SettingsButton>}>
      <div className={picked ? "inbox picked" : "inbox"}>
        <section className="card inbox-list">
          <div className="chips inbox-chips" role="group" aria-label="Show">
            <Chip pressed={status === "open"} onClick={() => show("open")}>
              Open
            </Chip>
            <Chip pressed={status === "done"} onClick={() => show("done")}>
              Done
            </Chip>
            <Chip pressed={unreadOnly} onClick={() => setUnreadOnly(!unreadOnly)}>
              Unread
            </Chip>
          </div>
          {!threads ? (
            <div className="card-body">
              <Skeleton rows={6} />
            </div>
          ) : threads.length === 0 ? (
            <p className="muted inbox-none">
              {unreadOnly ? "No unread messages." : status === "open" ? "All caught up." : "Nothing marked done yet."}
            </p>
          ) : (
            <ul className="inbox-rows">
              {threads.map((t) => (
                <ThreadButton key={t.id} t={t} picked={picked === t.id} onPick={() => setPicked(t.id)} />
              ))}
            </ul>
          )}
        </section>
        {picked ? (
          <Conversation key={picked} id={picked} onBack={() => setPicked(null)} onMoved={() => moveOn(picked)} />
        ) : (
          <section className="card inbox-convo inbox-empty">
            <EmptyState icon="message" title="Pick a conversation">
              Texts and emails with clients show here, with the reminders and invoices they got.
            </EmptyState>
          </section>
        )}
      </div>
    </Page>
  );
}

/**
 * The client page's Communication tab: the same list and conversation as the Inbox, only this client's texts and emails
 * (open and done), so the office can read back and reply without leaving the client.
 */
export function ClientInbox({ clientId, open = null }: { clientId: string; open?: string | null }) {
  // open: a conversation the header's Email or Text button just started, shown first.
  const [picked, setPicked] = useState<string | null>(open);
  const list = useClientThreads(clientId);
  const threads = list.data?.threads;
  useEffect(() => {
    if (picked || !threads?.length || !window.matchMedia(WIDE).matches) return;
    setPicked(threads[0]!.id);
  }, [picked, threads]);
  if (list.error) return <LoadError error={list.error} retry={() => void list.refetch()} />;
  if (threads?.length === 0 && !open)
    return (
      <section className="card">
        <EmptyState icon="message" title="No texts or emails yet">
          Texts and emails with this client show here once they start, with the reminders and invoices they got.
        </EmptyState>
      </section>
    );
  return (
    <div className={picked ? "inbox client-inbox picked" : "inbox client-inbox"}>
      <section className="card inbox-list">
        <div className="card-head">
          <h2>Conversations</h2>
        </div>
        {!threads ? (
          <div className="card-body">
            <Skeleton rows={4} />
          </div>
        ) : (
          <ul className="inbox-rows">
            {threads.map((t) => (
              <ThreadButton key={t.id} t={t} picked={picked === t.id} onPick={() => setPicked(t.id)} />
            ))}
          </ul>
        )}
      </section>
      {picked ? (
        <Conversation key={picked} id={picked} onBack={() => setPicked(null)} onMoved={() => void list.refetch()} />
      ) : null}
    </div>
  );
}

function ThreadButton({ t, picked, onPick }: { t: ThreadRow; picked: boolean; onPick: () => void }) {
  return (
    <li>
      <button
        type="button"
        className={t.unread ? "inbox-row unread" : "inbox-row"}
        aria-current={picked ? "true" : undefined}
        onClick={onPick}
      >
        <WhoCircle name={t.name} />
        <span className="inbox-row-main">
          <span className="inbox-row-top">
            <b>{who(t.name)}</b>
            <time dateTime={t.lastAt} title={ago(t.lastAt)}>
              {shortAgo(t.lastAt)}
            </time>
          </span>
          <span className="inbox-row-bottom">
            <span className="inbox-row-text">{t.lastText ?? ""}</span>
            {t.unread ? <span className="inbox-count num">{t.unread}</span> : null}
          </span>
        </span>
      </button>
    </li>
  );
}

function Conversation({ id, onBack, onMoved }: { id: string; onBack: () => void; onMoved: () => void }) {
  const t = useThread(id);
  const send = useReply();
  const undo = useUndoReply();
  const change = useThreadChange();
  const toast = useToast();
  const qc = useQueryClient();
  const { can, company } = useSession();
  const [offering, setOffering] = useState<PlanOfferOpener | "any" | null>(null);
  const [body, setBody] = useState("");
  const [note, setNote] = useState(false);
  const chat = useRef<HTMLOListElement>(null);
  const count = t.data?.messages.length ?? 0;

  // Opening a conversation marks it read on the server, so the list and the menu count catch up now, not in 30 seconds.
  const loaded = Boolean(t.data);
  useEffect(() => {
    if (loaded) void qc.invalidateQueries({ queryKey: ["inbox", "threads"] });
    if (loaded) void qc.invalidateQueries({ queryKey: ["inbox", "unread"] });
  }, [loaded, qc]);
  // The newest message is at the bottom, so the chat opens there and follows new ones.
  useEffect(() => {
    if (chat.current) chat.current.scrollTop = chat.current.scrollHeight;
  }, [count]);

  if (!t.data)
    return (
      <section className="card inbox-convo">
        <div className="card-body">
          <Skeleton rows={6} />
        </div>
      </section>
    );
  const d = t.data;
  const blocked = !note && d.stopped;
  const lastIn = d.messages.findLast((m) => m.direction === "in");
  const wantsPlan = Boolean(d.clientId) && company.trade === "cleaning" && can("quote.update") && asksForPlan(lastIn?.body);
  const go = () => {
    if (!body.trim() || blocked || send.isPending) return;
    send.mutate(
      { id, body: body.trim(), note },
      {
        onSuccess: (after) => {
          setBody("");
          if (note) return;
          // Undo send: the reply waits a few seconds on the server, so Undo can still stop it.
          const mine = after.messages.findLast((m) => m.direction === "out" && m.author === "team");
          if (mine)
            toast("Reply sending", {
              undo: () =>
                undo.mutate(mine.id, {
                  onSuccess: (r) => {
                    setBody(r.body);
                    toast("Not sent. Your words are back in the box.");
                  },
                  onError: (e) => toast(errText(e), { kind: "error" }),
                }),
            });
        },
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  };

  return (
    <section className="card inbox-convo">
      <div className="card-head">
        <div className="inbox-who">
          <button type="button" className="inbox-back" onClick={onBack} aria-label="Back to the list">
            <Icon name="arrowLeft" size={20} />
          </button>
          <WhoCircle name={d.name} large />
          <div>
            <h2>
              {d.clientId ? (
                <Link to="/clients/$id" params={{ id: d.clientId }}>
                  {who(d.name)}
                </Link>
              ) : (
                who(d.name)
              )}
            </h2>
            <span className="muted">
              {d.channel === "sms" ? prettyPhone(d.contact) : d.contact}
              {d.assignedTo ? (
                <>
                  {" "}
                  <StatusLabel status="informative">Assigned</StatusLabel>
                </>
              ) : null}
            </span>
          </div>
        </div>
        {d.clientId && company.trade === "cleaning" && can("quote.update") ? (
          // They texted asking about a schedule: their prices and a link, from right here (owner, 2026-11-02).
          <Button type="tertiary" size="small" onClick={() => setOffering("any")}>
            Offer a plan
          </Button>
        ) : null}
        {offering && d.clientId ? (
          <PlanOfferSend
            clientId={d.clientId}
            opener={offering === "any" ? undefined : offering}
            onClose={() => setOffering(null)}
          />
        ) : null}
        {can("client.update") ? (
          <Button
            type="secondary"
            size="small"
            loading={change.isPending}
            onClick={() =>
              change.mutate(
                { id, status: d.status === "open" ? "done" : "open" },
                { onSuccess: onMoved, onError: (e) => toast(errText(e), { kind: "error" }) },
              )
            }
          >
            {d.status === "open" ? "Mark done" : "Open again"}
          </Button>
        ) : null}
      </div>
      {wantsPlan ? (
        // Their last text asks about a regular cleaning (owner, 2026-11-02): offer it from right here.
        <div className="inbox-banner">
          <Banner type="notice">
            <b>They asked about a regular cleaning.</b> Send them a plan offer with their prices.{" "}
            <Button size="small" onClick={() => setOffering("texted")}>
              Send offer
            </Button>
          </Banner>
        </div>
      ) : null}
      {d.stopped ? (
        <div className="inbox-banner">
          <Banner type="warning">They texted STOP. Texts to them are off until they text START.</Banner>
        </div>
      ) : null}
      {d.messages.length === 0 ? (
        <div className="chat inbox-nochat">
          <p className="muted">
            No texts saved here yet. Texts from before the move to the new Steward are still in Quo. New texts and replies show
            here.
          </p>
        </div>
      ) : (
        <ol className="chat" ref={chat}>
          {d.messages.flatMap((m, i) => {
            const before = d.messages[i - 1];
            const fresh = !before || Date.parse(m.at) - Date.parse(before.at) >= GAP;
            // The name over a bubble only when the sender changes or after a stamp, so a run of texts reads as one.
            const named = fresh || before.from !== m.from || before.direction !== m.direction;
            const state =
              m.status === "dry_run"
                ? "Not sent (messages are off)"
                : m.status === "held"
                  ? "Waits for 8 AM"
                  : m.status === "queued"
                    ? "Sending"
                    : null;
            const bubble = (
              <li key={m.id} className={`bubble ${m.direction}`}>
                {named ? <span className="bubble-who">{m.from}</span> : null}
                <p title={ago(m.at)}>{m.body}</p>
                {state ? <span className="bubble-state">{state}</span> : null}
              </li>
            );
            return fresh
              ? [
                  <li key={`at-${m.id}`} className="chat-stamp">
                    <time dateTime={m.at}>{stamp(m.at)}</time>
                  </li>,
                  bubble,
                ]
              : [bubble];
          })}
        </ol>
      )}
      {can("client.update") ? (
        <div className={note ? "inbox-reply note" : "inbox-reply"}>
          <div className="inbox-compose">
            <textarea
              rows={2}
              aria-label={
                note ? "Note for the office" : `Reply by ${d.channel === "sms" ? "text" : "email"} from the business line`
              }
              placeholder={
                note
                  ? "Write a note for the office. The client never sees it."
                  : `${d.channel === "sms" ? "Text" : "Email"} ${who(d.name)} from the business line`
              }
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => {
                // Ctrl+Enter (Cmd+Enter on a Mac) sends, so a plain Enter can still start a new line.
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  go();
                }
              }}
            />
            <div className="inbox-compose-bar">
              <Button type="tertiary" size="small" onClick={() => setNote(!note)}>
                {note ? "Write a reply instead" : "Write a note instead"}
              </Button>
              <span className="inbox-hint">Ctrl+Enter sends</span>
              <Button size="small" loading={send.isPending} disabled={!body.trim() || blocked} onClick={go}>
                {note ? "Save note" : "Send"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
